import { useCallback, useRef, useState } from 'react';

/** Material UI Packages. */
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogContent,
    IconButton,
    LinearProgress,
    Paper,
    Typography,
} from '@mui/material';

/** Material UI Icons. */
import {
    UploadFile as UploadFileIcon,
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
} from '@mui/icons-material';

/** Auth0. */
import { useAuth0 } from '@auth0/auth0-react';

/** Internal. */
import { createShipmentFromDocument, DOCUMENT_STATUS } from 'shared/api/documentUploadApi';
import { getSocket } from 'socket';
import { setSnackbar } from 'shared/store/slices/snackBarSlice';
import { PaperComponent } from 'shared/ui/DraggableDialog';
import DialogHeader from 'shared/ui/DialogHeader';
import { DataGrid } from '@mui/x-data-grid';

import useDocumentGridColumns from './documentUploadColumns';
import { useDocumentParsing, useLocationAutocomplete, useMblHblSync } from './documentUploadHooks';
import {
    ACCEPT,
    MAX_FILES,
    dispatchCreatedShipment,
    getFileRows,
    getParsedDataBase,
    isHblOrHawb,
    isMblOrMawb,
    mergeHblRows,
    mergeMblRow,
} from './documentUploadUtils';

/** Queued files with per-file parsing status. */
function UploadFileList({ files, onRemoveFile, onManualFallback }) {
    if (files.length === 0) return null;
    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Files ({files.length})
            </Typography>
            {files.map((item) => (
                <Paper
                    key={item.id}
                    variant="outlined"
                    sx={{ p: 1.5, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                        <Typography noWrap sx={{ flex: 1 }}>
                            {item.name}
                        </Typography>
                        {item.status === DOCUMENT_STATUS.PENDING && (
                            <Typography variant="caption" color="text.secondary">
                                Pending
                            </Typography>
                        )}
                        {item.status === DOCUMENT_STATUS.PARSING && (
                            <Box sx={{ width: 80 }}>
                                <LinearProgress variant="determinate" value={item.progress} />
                            </Box>
                        )}
                        {item.status === DOCUMENT_STATUS.SUCCESS && <CheckCircleIcon color="primary" fontSize="small" />}
                        {item.status === DOCUMENT_STATUS.FAILED && (
                            <ErrorIcon color="error" fontSize="small" titleAccess={item.error} />
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {item.status === DOCUMENT_STATUS.FAILED && (
                            <Button size="small" color="primary" onClick={onManualFallback}>
                                Enter manually
                            </Button>
                        )}
                        <IconButton size="small" onClick={() => onRemoveFile(item.id)} aria-label="Remove">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Paper>
            ))}
        </Box>
    );
}

/** Editable preview of the parsed documents: MBL/MAWB row + HBL/HAWB list. */
function ParsedDataGrids({
    editedMblRow,
    editedHblRows,
    mblCols,
    hblCols,
    processMblRowUpdate,
    processHblRowUpdate,
    onRowUpdateError,
}) {
    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Parsed data
            </Typography>
            {editedMblRow && (
                <>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                        MBL / MAWB
                    </Typography>
                    <DataGrid
                        rows={[editedMblRow]}
                        columns={mblCols}
                        hideFooter
                        autoHeight
                        disableRowSelectionOnClick
                        editMode="cell"
                        processRowUpdate={processMblRowUpdate}
                        onProcessRowUpdateError={onRowUpdateError}
                        sx={{ minHeight: 52, mb: 1.5 }}
                    />
                </>
            )}
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                HBL / HAWB list
            </Typography>
            <DataGrid
                rows={editedHblRows}
                columns={hblCols}
                hideFooter
                autoHeight
                disableRowSelectionOnClick
                editMode="cell"
                processRowUpdate={processHblRowUpdate}
                onProcessRowUpdateError={onRowUpdateError}
                sx={{ minHeight: Math.min(220, 52 + editedHblRows.length * 52) }}
            />
        </Box>
    );
}

export default function DocumentUploadDialog({
    open,
    onClose,
    tabValue,
    dispatch,
    onOpenManualEntry,
    clientId,
    /** New Shipment 내부에서 열릴 때 true. Preview 후 "Apply to form"으로 폼에 반영 */
    embedInNewShipment = false,
    /** Detail panel에서 열릴 때 true. 모든 파일을 HBL/HAWB로만 처리, Apply 시 hblList만 전달 */
    embedInDetailPanel = false,
    /** embedInNewShipment 또는 embedInDetailPanel일 때, Preview 확인 후 호출. ({ mblData?, hblList }) => void */
    onApplyToForm,
}) {
    const { getAccessTokenSilently } = useAuth0();
    const fileInputRef = useRef(null);
    const [creatingId, setCreatingId] = useState(null);

    const socket = getSocket();
    const sid = socket?.id ?? '';

    const {
        files,
        successFiles,
        hasFailed,
        dragging,
        dragHandlers,
        removeFile,
        removeSuccessFiles,
        resetFiles,
        handleFileInputChange,
    } = useDocumentParsing({ open, tabValue, sid, getAccessTokenSilently });

    const { mblFile, editedMblRow, editedHblRows, processMblRowUpdate, processHblRowUpdate, resetEdits } =
        useMblHblSync({ successFiles, tabValue });

    const { vesselOptions, locationOptions, handleVesselInputChange, locationInputHandlers, resetOptions } =
        useLocationAutocomplete({ dispatch, tabValue });

    const { mblCols, hblCols } = useDocumentGridColumns({
        tabValue,
        vesselOptions,
        locationOptions,
        handleVesselInputChange,
        locationInputHandlers,
    });

    const handleRowUpdateError = useCallback(
        (error) => {
            dispatch(
                setSnackbar({
                    open: true,
                    message: error?.message || 'Could not save the edited cell. Please try again.',
                    severity: 'error',
                })
            );
        },
        [dispatch]
    );

    /** Parsed data of one file with the user's grid edits merged in, for "Create shipment". */
    const getMergedParsedDataForCreate = useCallback(
        (item) => {
            const base = getParsedDataBase(item);
            if (editedMblRow != null && isMblOrMawb(item.document_type)) {
                return mergeMblRow(base, editedMblRow, tabValue);
            }
            if (isHblOrHawb(item.document_type)) {
                return mergeHblRows(base, getFileRows(editedHblRows, item.id), tabValue);
            }
            return base;
        },
        [tabValue, editedMblRow, editedHblRows]
    );

    const handleCreateShipment = useCallback(
        async (item) => {
            if (!item.parsedData || !clientId) {
                dispatch(
                    setSnackbar({
                        open: true,
                        message: 'Client is required. Please select a client or use manual entry.',
                        severity: 'warning',
                    })
                );
                return;
            }
            setCreatingId(item.id);
            const parsedData = getMergedParsedDataForCreate(item);
            try {
                const result = await createShipmentFromDocument({
                    parsedData,
                    shipmentType: tabValue,
                    clientId,
                    overrides: {},
                    sid,
                    getAccessTokenSilently,
                });
                if (result.status === 200 && result.data) {
                    dispatchCreatedShipment(dispatch, tabValue, result.data);
                    dispatch(
                        setSnackbar({
                            open: true,
                            message: result.message || 'Shipment created. It will appear in the table.',
                            severity: 'success',
                        })
                    );
                    removeFile(item.id);
                } else {
                    dispatch(
                        setSnackbar({
                            open: true,
                            message: result.message || 'Create failed',
                            severity: 'warning',
                        })
                    );
                }
            } catch (err) {
                dispatch(
                    setSnackbar({
                        open: true,
                        message: err.response?.data?.message || err.message || 'Create failed',
                        severity: 'error',
                    })
                );
            } finally {
                setCreatingId(null);
            }
        },
        [tabValue, clientId, sid, dispatch, getAccessTokenSilently, removeFile, getMergedParsedDataForCreate]
    );

    /** Hand the merged MBL + HBL data to the parent form (New Shipment / Detail panel). */
    const handleApplyToForm = useCallback(() => {
        const mblData = mblFile ? { parsedData: mergeMblRow(getParsedDataBase(mblFile), editedMblRow, tabValue) } : null;
        const fileIds = [...new Set(editedHblRows.map((row) => row.fileId))];
        const hblList = fileIds.reduce((acc, fileId) => {
            const file = successFiles.find((f) => f.id === fileId);
            if (file) {
                acc.push({
                    parsedData: mergeHblRows(getParsedDataBase(file), getFileRows(editedHblRows, fileId), tabValue),
                });
            }
            return acc;
        }, []);

        onApplyToForm({ mblData, hblList });
        removeSuccessFiles();
        resetEdits();
        onClose();
    }, [
        mblFile,
        editedMblRow,
        editedHblRows,
        successFiles,
        tabValue,
        onApplyToForm,
        removeSuccessFiles,
        resetEdits,
        onClose,
    ]);

    const handleManualFallback = useCallback(() => {
        onClose();
        onOpenManualEntry?.();
    }, [onClose, onOpenManualEntry]);

    const handleClose = useCallback(() => {
        onClose();
        resetFiles();
        resetEdits();
        resetOptions();
    }, [onClose, resetFiles, resetEdits, resetOptions]);

    const dropZoneSx = {
        py: 2,
        px: 1,
        border: '2px dashed',
        borderColor: dragging ? 'primary.main' : 'divider',
        borderRadius: 2,
        bgcolor: dragging ? 'action.hover' : 'background.default',
        mb: 2,
        textAlign: 'center',
    };

    const showApplyButton = (embedInNewShipment || embedInDetailPanel) && onApplyToForm && successFiles.length > 0;
    const showCreateButton = Boolean((!embedInNewShipment && !embedInDetailPanel) || clientId);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperComponent={PaperComponent}
            aria-labelledby="document-upload-dialog"
        >
            <DialogHeader id="document-upload-dialog" onClose={handleClose} title="Upload PDF documents" />
            <DialogContent>
                <Box sx={dropZoneSx} {...dragHandlers}>
                    <UploadFileIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        <b>Drop PDFs here</b> or{' '}
                        <Typography
                            component="span"
                            onClick={() => fileInputRef.current?.click()}
                            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                        >
                            browse
                        </Typography>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Multiple files supported (max {MAX_FILES}). Parsed data can be reviewed before creating.
                    </Typography>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPT}
                        multiple
                        hidden
                        onChange={handleFileInputChange}
                    />
                </Box>

                <UploadFileList files={files} onRemoveFile={removeFile} onManualFallback={handleManualFallback} />

                {hasFailed && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Some documents could not be parsed. Use <strong>Enter manually</strong> to create a shipment
                        with the New Shipment form.
                    </Alert>
                )}

                {/* Parsed data: MBL + HBL list in one view (no per-file preview) */}
                {successFiles.length > 0 && (
                    <ParsedDataGrids
                        editedMblRow={editedMblRow}
                        editedHblRows={editedHblRows}
                        mblCols={mblCols}
                        hblCols={hblCols}
                        processMblRowUpdate={processMblRowUpdate}
                        processHblRowUpdate={processHblRowUpdate}
                        onRowUpdateError={handleRowUpdateError}
                    />
                )}

                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    {showApplyButton && (
                        <Button variant="contained" color="primary" onClick={handleApplyToForm}>
                            {embedInDetailPanel ? 'Apply to shipment' : 'Apply to form'}
                        </Button>
                    )}
                    {showCreateButton && (
                        <Button
                            variant="contained"
                            disabled={!successFiles[0] || !!creatingId}
                            onClick={() => successFiles[0] && handleCreateShipment(successFiles[0])}
                        >
                            {creatingId ? 'Creating…' : 'Create shipment'}
                        </Button>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
}
